import * as AntdIcons from '@ant-design/icons';
import { Button, Input, Popover, Segmented, Space } from 'antd';
import { createStyles } from 'antd-style';
import React, {
  memo,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

type IconPickerProps = {
  value?: string;
  onChange?: (value?: string) => void;
  iconKeys?: string[];
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
};

type IconRegistryItem = {
  name: string;
  component: React.ComponentType;
};

type IconTheme = 'Outlined' | 'Filled' | 'TwoTone';

const iconRegistryMap = Object.fromEntries(
  Object.entries(AntdIcons as unknown as Record<string, unknown>)
    .filter(
      ([name, iconComponent]) =>
        (typeof iconComponent === 'function' ||
          typeof iconComponent === 'object') &&
        iconComponent &&
        (name.endsWith('Outlined') ||
          name.endsWith('Filled') ||
          name.endsWith('TwoTone')),
    )
    .map(([name, iconComponent]) => [
      name,
      { name, component: iconComponent as React.ComponentType },
    ]),
) as Record<string, IconRegistryItem>;

const defaultIconNames = Object.keys(iconRegistryMap).sort((a, b) =>
  a.localeCompare(b),
);

const resolveTheme = (iconName?: string): IconTheme => {
  if (iconName?.endsWith('Filled')) return 'Filled';
  if (iconName?.endsWith('TwoTone')) return 'TwoTone';
  return 'Outlined';
};

const useStyles = createStyles(({ token }) => ({
  iconButton: {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    padding: '10px 4px',
    cursor: 'pointer',
    background: token.colorBgContainer,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
  },
  iconButtonSelected: {
    border: `1px solid ${token.colorPrimary}`,
    background: token.colorPrimaryBg,
  },
  emptyText: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    color: token.colorTextQuaternary,
    padding: '20px 0',
  },
}));

type IconPanelProps = {
  value?: string;
  theme: IconTheme;
  onThemeChange: (theme: IconTheme) => void;
  visibleIconNames: string[];
  onSelect: (iconName: string) => void;
  searchText: string;
  onSearchChange: (value: string) => void;
};

const IconPanel = memo<IconPanelProps>(
  ({
    value,
    theme,
    onThemeChange,
    visibleIconNames,
    onSelect,
    searchText,
    onSearchChange,
  }) => {
    const { styles, cx } = useStyles();
    return (
      <div style={{ width: 'min(92vw, 560px)' }}>
        <Input
          allowClear
          value={searchText}
          placeholder="搜索图标，例如：SettingOutlined"
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <Segmented<IconTheme>
          style={{ width: '100%', marginTop: 8 }}
          block
          value={theme}
          onChange={onThemeChange}
          options={[
            {
              label: '线框',
              value: 'Outlined',
              icon: <AntdIcons.BorderOutlined />,
            },
            { label: '实底', value: 'Filled', icon: <AntdIcons.StopFilled /> },
            {
              label: '双色',
              value: 'TwoTone',
              icon: <AntdIcons.HighlightTwoTone />,
            },
          ]}
        />
        <div
          style={{
            marginTop: 8,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
            gap: 8,
            maxHeight: 'min(52vh, 360px)',
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {visibleIconNames.length ? (
            visibleIconNames.map((iconName) => {
              const IconComponent = iconRegistryMap[iconName].component;
              return (
                <button
                  type="button"
                  key={iconName}
                  onClick={() => onSelect(iconName)}
                  title={iconName}
                  className={cx(
                    styles.iconButton,
                    iconName === value && styles.iconButtonSelected,
                  )}
                >
                  <IconComponent />
                </button>
              );
            })
          ) : (
            <div className={styles.emptyText}>当前分类下没有匹配图标</div>
          )}
        </div>
      </div>
    );
  },
);

export const renderIcon = (iconName?: string): React.ReactNode => {
  if (!iconName) return undefined;
  const item = iconRegistryMap[iconName];
  if (!item) return undefined;
  const Icon = item.component;
  return <Icon />;
};

const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  iconKeys,
  disabled = false,
  allowClear = true,
  placeholder = '请选择图标',
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<IconTheme>(resolveTheme(value));
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);

  useEffect(() => {
    setTheme(resolveTheme(value));
  }, [value]);

  const candidateIconNames = useMemo(() => {
    if (!iconKeys?.length) return defaultIconNames;
    return iconKeys.filter((iconName) => Boolean(iconRegistryMap[iconName]));
  }, [iconKeys]);

  const visibleIconNames = useMemo(() => {
    const keyword = deferredSearchText.trim().toLowerCase();
    return candidateIconNames.filter((iconName) => {
      if (!iconName.endsWith(theme)) return false;
      if (!keyword) return true;
      return iconName.toLowerCase().includes(keyword);
    });
  }, [candidateIconNames, deferredSearchText, theme]);

  const CurrentIcon = value
    ? (iconRegistryMap[value]?.component ?? AntdIcons.EllipsisOutlined)
    : AntdIcons.EllipsisOutlined;

  const handleSelect = (iconName: string) => {
    onChange?.(iconName);
    setOpen(false);
  };

  const containerStyle: React.CSSProperties = {
    minWidth: style?.width || style?.minWidth ? undefined : 320,
    ...style,
  };

  return (
    <Popover
      title="选择图标"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      content={
        <IconPanel
          value={value}
          theme={theme}
          onThemeChange={setTheme}
          visibleIconNames={visibleIconNames}
          onSelect={handleSelect}
          searchText={searchText}
          onSearchChange={setSearchText}
        />
      }
    >
      <Space.Compact style={containerStyle}>
        <Input
          readOnly
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: 0 }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          suffix={
            allowClear ? (
              <AntdIcons.CloseCircleFilled
                style={{ visibility: value ? 'visible' : 'hidden', cursor: 'pointer' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  if (!value) return;
                  e.stopPropagation();
                  onChange?.(undefined);
                  setOpen(false);
                }}
              />
            ) : null
          }
        />
        <Button
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) setOpen((prev) => !prev);
          }}
        >
          <CurrentIcon />
          <AntdIcons.DownOutlined
            style={{
              transition: 'transform 0.2s ease',
              transform: `rotate(${open ? 180 : 0}deg)`,
            }}
          />
        </Button>
      </Space.Compact>
    </Popover>
  );
};

export default IconPicker;
